import React from 'react';

/* ─── Style helpers ─── */
const badgeStyle = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px 8px',
  borderRadius: 4,
  background: bg,
  color,
  fontSize: 10,
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
});

const COLORS = {
  green: { bg: 'rgba(74, 222, 128, 0.15)', text: '#4ade80' },
  blue: { bg: 'rgba(59, 130, 246, 0.15)', text: '#60a5fa' },
  orange: { bg: 'rgba(249, 115, 22, 0.15)', text: '#fb923c' },
  yellow: { bg: 'rgba(251, 191, 36, 0.15)', text: '#fbbf24' },
  red: { bg: 'rgba(248, 113, 113, 0.15)', text: '#f87171' },
  redStrong: { bg: 'rgba(248, 113, 113, 0.2)', text: '#f87171' },
  muted: { bg: 'var(--bg-input)', text: 'var(--text-muted)' },
};

/* ─── Liquidity Badge (global) ─── */
interface LiquidityBadgeProps {
  grade?: string | null;
}

export const LiquidityBadge: React.FC<LiquidityBadgeProps> = ({ grade }) => {
  let label: string;
  let c: { bg: string; text: string };

  if (grade === undefined || grade === null) {
    label = 'N/A'; c = COLORS.muted;
  } else if (grade === 'EXCELLENTE') {
    label = 'Excellente'; c = COLORS.green;
  } else if (grade === 'FRAGILE') {
    label = 'Fragile'; c = COLORS.blue;
  } else if (grade === 'FAIBLE') {
    label = 'Faible'; c = COLORS.orange;
  } else {
    label = 'Illiquide'; c = COLORS.red;
  }

  return <span style={badgeStyle(c.bg, c.text)}>{label}</span>;
};

/* ─── Liquidity Long-Term Badge ─── */
interface LiqTermBadgeProps {
  grade?: string | null;
  score?: number | null;
}

const emojiForGrade = (grade: string | null | undefined): { emoji: string; c: { bg: string; text: string } } => {
  if (grade === 'EXCELLENTE') return { emoji: '🟢', c: COLORS.green };
  if (grade === 'FRAGILE') return { emoji: '🟡', c: COLORS.blue };
  if (grade === 'FAIBLE') return { emoji: '🟠', c: COLORS.orange };
  if (grade) return { emoji: '🔴', c: COLORS.red };
  return { emoji: 'N/A', c: COLORS.muted };
};

export const LiqLongTermBadge: React.FC<LiqTermBadgeProps> = ({ grade, score }) => {
  const { emoji, c } = emojiForGrade(grade);
  return (
    <span
      title={`Long Terme: ${grade ?? 'N/A'} (${score ?? '—'}/100)`}
      style={{ ...badgeStyle(c.bg, c.text), fontSize: 12, padding: '2px 6px' }}
    >
      {emoji} {score || ''}
    </span>
  );
};

/* ─── Liquidity Short-Term Badge ─── */
interface LiqCourtTermBadgeProps extends LiqTermBadgeProps {
  isBlocked?: boolean;
}

export const LiqCourtTermBadge: React.FC<LiqCourtTermBadgeProps> = ({ grade, score, isBlocked }) => {
  if (isBlocked) {
    return (
      <span
        title={`Court Terme: ${grade ?? 'N/A'} (${score ?? '—'}/100) - BLOQUÉ`}
        style={{ ...badgeStyle(COLORS.redStrong.bg, COLORS.redStrong.text), fontSize: 12, padding: '2px 6px' }}
      >
        ⛔ {score || ''}
      </span>
    );
  }
  const { emoji, c } = emojiForGrade(grade);
  return (
    <span
      title={`Court Terme: ${grade ?? 'N/A'} (${score ?? '—'}/100)`}
      style={{ ...badgeStyle(c.bg, c.text), fontSize: 12, padding: '2px 6px' }}
    >
      {emoji} {score || ''}
    </span>
  );
};

/* ─── Dividend Regularity Badge ─── */
interface DivRegularityBadgeProps {
  score: number;
}

export const DivRegularityBadge: React.FC<DivRegularityBadgeProps> = ({ score }) => {
  let label: string;
  let c: { bg: string; text: string };

  if (score === 5) {
    label = 'Très régulier'; c = COLORS.green;
  } else if (score >= 3) {
    label = 'Régulier'; c = COLORS.yellow;
  } else if (score >= 1) {
    label = 'Peu régulier'; c = COLORS.orange;
  } else {
    label = 'Non régulier'; c = COLORS.red;
  }

  return <span style={badgeStyle(c.bg, c.text)}>{label}</span>;
};

/* ─── Tech Score Badge ─── */
interface TechScoreBadgeProps {
  score: number;
}

export const TechScoreBadge: React.FC<TechScoreBadgeProps> = ({ score }) => {
  let c: { bg: string; text: string };
  if (score >= 70) c = COLORS.green;
  else if (score <= 40) c = COLORS.red;
  else c = COLORS.blue;

  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: 10,
      background: c.bg,
      color: c.text,
      fontWeight: 'bold',
      fontSize: 11,
    }}>
      {score}/100
    </span>
  );
};

/* ─── Bollinger Band Signal Badge ─── */
interface BBSignalBadgeProps {
  signal?: string | null;
}

export const BBSignalBadge: React.FC<BBSignalBadgeProps> = ({ signal }) => {
  if (!signal) return <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>—</span>;

  let c: { bg: string; text: string };
  if (signal === 'SURACHETÉ' || signal === 'OVERBOUGHT') c = COLORS.red;
  else if (signal === 'SURVENDU' || signal === 'OVERSOLD') c = COLORS.green;
  else c = COLORS.blue;

  return <span style={badgeStyle(c.bg, c.text)}>{signal}</span>;
};
